import {
  useDownloadTransactionRowLevelDetails,
  useGetOneTransaction,
  useGetOneTransactionDetail,
  useOriginalSubmissionFileDownload,
} from "#hooks/adapters/useTransactions";
import {
  useClientSideSortingAndPagination,
  useTableSortingAndPagination,
} from "#redux/reducers/tableStateReducer";
import { useParams } from "#routers/hooks";
import {
  extractItemsFromTransactionDetails,
  extractTransactionItemData,
  getTransactionDetailStatusPillToggleVariant,
  getTransactionDetailStatusPillVariant,
} from "#routes/data-tools/submissions/datasets/[transactionId]/detail/DataSetSubmissionDetail.helpers";
import { DataSetSubmissionErrorDrawer } from "#routes/data-tools/submissions/datasets/[transactionId]/detail/DataSetSubmissionErrorDrawer";
import { type DataSetSubmissionDetailRoutePath } from "#routes/data-tools/submissions/datasets/[transactionId]/detail/index";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { DataSetSubmissionRowTableErrorDataDisplay } from "#src/routes/data-tools/submissions/datasets/[transactionId]/detail/DataSetSubmissionRowTableErrorDataDisplay";
import { ExceptionUtils } from "#src/utils/exception";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  ButtonWithPopover,
  DataTable,
  DataTablePanel,
  Dialog,
  DropdownMenu,
  EmptyState,
  HeaderType,
  Icon,
  IconLoading,
  Link,
  Panel,
  Pill,
  PillToggleGroup,
  TextDataDisplay,
  useToast,
  type EmptyStateProps,
  type PanelProps,
} from "@validereinc/common-components";
import {
  Resources,
  SortDirection,
  TransactionAdapter,
  TransactionDetailItemStatus,
  TransactionStatus,
  type TransactionType,
} from "@validereinc/domain";
import {
  Transactions,
  type DataSetSubmissionTabularDataRowType,
} from "@validereinc/domain-controllers";
import React, { useEffect, useMemo, useState } from "react";

const { selectFirstAndLastTransactionState } = Transactions.logic;
const {
  DataSetSubmissionTabularDataCellStyleVariantsMap,
  getDataSetSubmissionTabularCellError,
  getFailedRowsFromTransactionDetails,
  getReadableDataSetSubmissionErrorType,
  getStepDisplayLabel,
  getTransactionDetailStatusDisplayLabelBasedOnStep,
  getTransactionDetailStatusesBasedOnStep,
  getWhichCellStyleVariantForCellInDataSetSubmissionTabularRow,
  getWhichCellStyleVariantForRowInDataSetSubmissionTabularRow,
} = Transactions.view;

const DEFAULT_FILTER_KEY = "all";

export const DataSetSubmissionRowTable = ({
  panelProps,
  transactionStep,
  transactionStatus,
}: {
  panelProps?: Omit<PanelProps, "children">;
  transactionStep: TransactionType["step"] | undefined;
  transactionStatus: TransactionType["status"] | undefined;
}) => {
  const { transactionId } =
    useParams<
      Exclude<typeof DataSetSubmissionDetailRoutePath.pathParams, undefined>
    >();

  const [tableConfigStorageKey, tableStateStorageKey] = useStorageKey(
    ["table-configuration", "table-state"],
    ["dataset", "submission", transactionId ?? "", "table"]
  );
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const transactionDetailStatuses = useMemo(
    () => getTransactionDetailStatusesBasedOnStep(transactionStep),
    [transactionStep]
  );
  const [isRowLevelDetailShown, setIsRowLevelDetailShown] = useState(false);
  const [isRetryConfirmationOpen, setIsRetryConfirmationOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState([DEFAULT_FILTER_KEY]);
  const [errorDrawerIndex, setErrorDrawerIndex] = useState<number>();
  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "index",
      sortDirection: SortDirection.ASCENDING,
    },
    undefined,
    tableStateStorageKey
  );

  const transactionQuery = useGetOneTransaction({
    id: transactionId ?? "",
    meta: { history: true },
  });
  const transactionLastStepQuery = useGetOneTransaction(
    {
      id: transactionId ?? "",
    },
    {
      enabled: !!transactionQuery.error,
    }
  );
  const { firstTransaction, lastTransaction } =
    selectFirstAndLastTransactionState(
      transactionQuery.data?.data ?? transactionLastStepQuery.data?.data
    );
  const {
    mutate: downloadTransactionRowLevelDetails,
    isLoading: areTransactionRowLevelDetailsDownloading,
  } = useDownloadTransactionRowLevelDetails();
  const originalFileSubmissionDownloadMutation =
    useOriginalSubmissionFileDownload();
  const {
    data,
    isLoading: isLoadingTransactionRowLevelDetails,
    error: errorTransactionRowLevelDetails,
    refetch: refetchTransactionRowLevelDetails,
  } = useGetOneTransactionDetail();
  const detailItems = extractItemsFromTransactionDetails(data);

  const retryTransaction = useMutation({
    mutationFn: (
      params: Parameters<typeof TransactionAdapter.retry.createOne>[0]
    ) => {
      return TransactionAdapter.retry.createOne(params);
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: [Resources.TRANSACTION] });
    },
    onError: (err) => {
      ExceptionUtils.reportException(err, "error", {
        hint: "Failed to re-try step in dataset submissions details page",
      });
      toast.push({
        description: "Failed to re-try step.",
        intent: "error",
      });
    },
  });

  const { items, pagination } = useClientSideSortingAndPagination(
    detailItems.reduce((total: DataSetSubmissionTabularDataRowType[], item) => {
      if (
        [DEFAULT_FILTER_KEY, item.status].some((status) =>
          statusFilter.includes(status)
        )
      ) {
        total.push({
          ...item,
          index: parseInt(item.index), // Casting is necessary for client-side sorting using esToolkit.orderBy
        });
      }
      return total;
    }, []),
    tableState
  );

  const getEmptyStateProps = (): EmptyStateProps | undefined => {
    const errorDetails = lastTransaction?.details ?? "";
    const viewDataAction = (
      <Button
        variant="primary"
        onClick={() => {
          queryClient.refetchQueries({
            queryKey: [Resources.TRANSACTION],
          });
          setIsRowLevelDetailShown(true);
        }}
        isLoading={isLoadingTransactionRowLevelDetails}
        slotLeft={
          errorTransactionRowLevelDetails ? (
            <Icon variant="warning-octagon" />
          ) : null
        }
      >
        View Data
      </Button>
    );
    const actionsForStepsWithPotentialData = (
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {viewDataAction}
      </div>
    );

    switch (lastTransaction?.step) {
      case "transaction_recorder":
      case "vulnerability_scanner": {
        const stepDisplayLabel = getStepDisplayLabel("transaction_recorder");

        switch (lastTransaction?.status) {
          case TransactionStatus.FAILED:
            return {
              title: `Failed to complete ${stepDisplayLabel}`,
              suggestion: errorDetails,
              icon: <Icon variant="smiley-sad" />,
              variant: "error",
            };
          case TransactionStatus.PENDING:
            return {
              variant: "info",
              icon: <IconLoading speed="slow" />,
              title: `${stepDisplayLabel} in-progress`,
              suggestion: "Recording and scanning the submission...",
            };
          default:
            return;
        }
      }
      case "archiver": {
        const stepDisplayLabel = getStepDisplayLabel("archiver");

        switch (lastTransaction?.status) {
          case TransactionStatus.FAILED:
            return {
              title: `Failed to complete ${stepDisplayLabel}`,
              suggestion: errorDetails,
              icon: <Icon variant="smiley-sad" />,
              variant: "error",
              action: actionsForStepsWithPotentialData,
            };
          case TransactionStatus.PENDING:
            return {
              variant: "info",
              icon: <IconLoading speed="slow" />,
              title: `${stepDisplayLabel} in-progress`,
              suggestion: "Validating and backing up the submission...",
            };
          default:
            return;
        }
      }
      case "operator": {
        const stepDisplayLabel = getStepDisplayLabel("operator");

        switch (lastTransaction?.status) {
          case TransactionStatus.FAILED:
            return {
              title: `Failed to complete ${stepDisplayLabel}`,
              suggestion: errorDetails,
              icon: <Icon variant="smiley-sad" />,
              variant: "error",
              action: actionsForStepsWithPotentialData,
            };
          case TransactionStatus.PENDING:
            return {
              variant: "info",
              icon: <IconLoading speed="slow" />,
              title: `${stepDisplayLabel} in-progress`,
              suggestion:
                "The transformations applied are determined by the target Dataset.",
            };
          default:
            return;
        }
      }
      case "sink": {
        const stepDisplayLabel = getStepDisplayLabel("sink");

        switch (lastTransaction?.status) {
          case TransactionStatus.FAILED:
            return {
              title: `Failed to complete ${stepDisplayLabel}`,
              suggestion: errorDetails,
              icon: <Icon variant="smiley-sad" />,
              variant: "error",
              action: actionsForStepsWithPotentialData,
            };
          case TransactionStatus.PENDING:
            return {
              variant: "info",
              icon: <IconLoading speed="slow" />,
              title: `${stepDisplayLabel} in-progress`,
              suggestion: "Ingesting the submission data into the database...",
            };
          case TransactionStatus.COMPLETED: {
            const successCount = data?.data.at(-1)?.success_count;

            return {
              variant: "success",
              icon: <Icon variant="seal-check" />,
              title: `Successfully completed ${stepDisplayLabel}`,
              suggestion: `${successCount !== undefined ? `${successCount} rows have been` : "The submission data has been"} ingested into the database.`,
              action: viewDataAction,
            };
          }
          case TransactionStatus.COMPLETED_WITH_PARTIAL_SUCCESS: {
            const successCount = data?.data.at(-1)?.success_count;
            const failureCount = data?.data.at(-1)?.failure_count;

            return {
              variant: "warning",
              icon: <Icon variant="seal-check" />,
              title: `Partially completed ${stepDisplayLabel}`,
              suggestion: `${successCount !== undefined && failureCount !== undefined ? `${successCount} rows have been successfully ingested and ${failureCount} rows have not been ingested into the database.` : "The submission data has been partially ingested into the database."} View data to see more details.`,
              action: viewDataAction,
            };
          }
          default:
            return;
        }
      }
      default:
        return;
    }
  };

  useEffect(() => {
    if (!data?.data.length) return;

    setIsRowLevelDetailShown(true);
  }, [data]);

  useEffect(() => {
    if (lastTransaction?.step !== "sink") return;
    if (lastTransaction.state === "pending") return;

    refetchTransactionRowLevelDetails();
    setIsRowLevelDetailShown(true);
  }, [lastTransaction, refetchTransactionRowLevelDetails]);

  // Assume the first row contains all the headers
  const parsedHeaders: Array<HeaderType<DataSetSubmissionTabularDataRowType>> =
    Object.keys(extractTransactionItemData(items[0]?.data ?? "{}")).map(
      (label) => ({
        key: `data.${label}`,
        label: label,
        renderComponent: ({ item, isLoading, isBusy }) => {
          const errDetails = getDataSetSubmissionTabularCellError(item, label);
          const data = extractTransactionItemData(item.data ?? "{}");

          if (item.status === "failed" && errDetails) {
            return (
              <DataSetSubmissionRowTableErrorDataDisplay
                isLoading={isLoading}
                isBusy={isBusy}
                intent="error"
                errorType={getReadableDataSetSubmissionErrorType(
                  errDetails.validation_type
                )}
                errorMessage={errDetails.error_message}
                errorSuggestions={errDetails.validation_value ?? ""}
              >
                <TextDataDisplay
                  isLoading={isLoading}
                  isBusy={isBusy}
                  value={data[label] as string}
                  style={{
                    overflowWrap: "break-word",
                    wordBreak: "break-word",
                    whiteSpace: "pre-wrap",
                  }}
                />
              </DataSetSubmissionRowTableErrorDataDisplay>
            );
          }

          return (
            <DataTable.DataRow.TextCell
              isLoading={isLoading}
              isBusy={isBusy}
              value={data[label] as string}
              style={{
                overflowWrap: "break-word",
                wordBreak: "break-word",
                whiteSpace: "pre-wrap",
              }}
            />
          );
        },
        getStyle: ({ item }) => {
          const styleVariantToApply =
            getWhichCellStyleVariantForCellInDataSetSubmissionTabularRow(
              item,
              label
            );

          if (styleVariantToApply === "default") return {};

          return {
            backgroundColor:
              DataSetSubmissionTabularDataCellStyleVariantsMap[
                `${styleVariantToApply}_light`
              ],
            color:
              DataSetSubmissionTabularDataCellStyleVariantsMap[
                `${styleVariantToApply}_dark`
              ],
            fontWeight: "bold",
          };
        },
      })
    );

  const headers: Array<HeaderType<DataSetSubmissionTabularDataRowType>> = [
    {
      key: "index",
      label: "Row",
      isSortable: true,
      isFixed: true,
      renderComponent: ({ item }) => {
        if (item.error) {
          const styleVariantToApply =
            getWhichCellStyleVariantForRowInDataSetSubmissionTabularRow(item);

          return (
            <Link
              anchorTagProps={{
                style:
                  styleVariantToApply === "default"
                    ? {}
                    : {
                        color:
                          DataSetSubmissionTabularDataCellStyleVariantsMap[
                            `${styleVariantToApply}_dark`
                          ],
                        fontWeight: "bold",
                      },
              }}
              label={`#${item.index + 1}`}
              onClick={() => {
                setErrorDrawerIndex(
                  errors.findIndex(({ index }) => index == item.index)
                );
              }}
            />
          );
        } else {
          return `#${item.index + 1}`;
        }
      },
      getStyle: ({ item }) => {
        const styleVariantToApply =
          getWhichCellStyleVariantForRowInDataSetSubmissionTabularRow(item);

        if (styleVariantToApply === "default") return {};

        return {
          backgroundColor:
            DataSetSubmissionTabularDataCellStyleVariantsMap[
              `${styleVariantToApply}_light`
            ],
          color:
            DataSetSubmissionTabularDataCellStyleVariantsMap[
              `${styleVariantToApply}_dark`
            ],
          fontWeight: "bold",
        };
      },
    },
    {
      key: "status",
      label: "Status",
      isSortable: true,
      isFixed: true,
      renderComponent: ({ item }) => (
        <Pill variant={getTransactionDetailStatusPillVariant(item.status)}>
          {getTransactionDetailStatusDisplayLabelBasedOnStep(
            item.status,
            transactionStep
          )}
        </Pill>
      ),
    },
    ...parsedHeaders,
  ];

  const pillToggles = detailItems?.length
    ? [
        {
          name: DEFAULT_FILTER_KEY,
          label: "All",
          value: DEFAULT_FILTER_KEY,
          count: detailItems.length,
          isSelected:
            !statusFilter?.length || statusFilter?.includes(DEFAULT_FILTER_KEY),
          shouldSelectAll: true,
        },
        ...transactionDetailStatuses.map((status) => ({
          name: status,
          label: getTransactionDetailStatusDisplayLabelBasedOnStep(
            status,
            transactionStep
          ),
          value: status,
          count: detailItems.filter((item) => item.status === status).length,
          variant: getTransactionDetailStatusPillToggleVariant(status),
          isSelected: statusFilter?.includes(status) ?? false,
        })),
      ]
    : [];

  const isExpiredTransaction =
    !!transactionQuery.error && !!transactionLastStepQuery.data?.data[0];
  const emptyStateProps = getEmptyStateProps();
  const hasErrors = !!getFailedRowsFromTransactionDetails(detailItems).length;
  const errors = items.filter(
    ({ status }) => status === TransactionDetailItemStatus.FAILED
  );

  const actionRow = [
    <Button
      key="summary"
      variant="outline"
      icon="eye"
      onClick={() => setIsRowLevelDetailShown(false)}
    >
      View Summary
    </Button>,
    ...(data?.data.length
      ? [
          <DropdownMenu
            key="download-opts"
            contentProps={{
              align: "end",
            }}
            options={[
              {
                label:
                  transactionStep === "operator" || transactionStep === "sink"
                    ? `Failed transformed rows`
                    : `Failed rows`,
                isDisabled:
                  originalFileSubmissionDownloadMutation.isLoading ||
                  !transactionId,
                onClick: () => {
                  if (!transactionId) return;

                  originalFileSubmissionDownloadMutation.mutate(
                    {
                      transactionId,
                      fileName: firstTransaction?.original_file_name ?? "",
                      onlyErrors: true,
                      hasTransformed:
                        transactionStep === "operator" ||
                        transactionStep === "sink"
                          ? true
                          : false,
                    },
                    {
                      onError: () => {
                        if (!isExpiredTransaction) return;

                        toast.push({
                          intent: "error",
                          description:
                            "Could not download submission because the transaction is expired (its older than 90 days)",
                        });
                      },
                    }
                  );
                },
              },
              {
                label: "Failed rows with error details",
                isDisabled:
                  areTransactionRowLevelDetailsDownloading || !transactionId,
                onClick: () => {
                  if (!transactionId) return;

                  downloadTransactionRowLevelDetails({
                    id: transactionId,
                    fileName: `dataset_submission_${transactionId}_row_level_details.csv`,
                    meta: {
                      status: "failed",
                    },
                  });
                },
              },
            ]}
          >
            <Button
              icon="arrow-square-out"
              slotRight={<Icon variant="caret-down" />}
              variant="outline"
              isLoading={
                originalFileSubmissionDownloadMutation.isLoading ||
                areTransactionRowLevelDetailsDownloading
              }
            >
              Export
            </Button>
          </DropdownMenu>,
          ...(hasErrors &&
          (transactionStep === "operator" || transactionStep === "sink") &&
          (transactionStatus === "failed" ||
            transactionStatus === "completed_with_partial_success")
            ? [
                <ButtonWithPopover
                  key="resubmit"
                  buttonProps={{
                    icon: "arrow-counter-clockwise",
                    variant: "primary",
                    onClick: () => setIsRetryConfirmationOpen(true),
                    isLoading: retryTransaction.isLoading,
                    disabled: isExpiredTransaction,
                  }}
                  label="Only failed rows will be retried."
                >
                  Retry
                </ButtonWithPopover>,
              ]
            : []),
        ]
      : []),
  ];

  return (
    <>
      <DataSetSubmissionErrorDrawer
        items={errors}
        itemIndex={errorDrawerIndex}
        setItemIndex={setErrorDrawerIndex}
      />
      <Dialog
        isOpen={isRetryConfirmationOpen}
        onClose={() => setIsRetryConfirmationOpen(false)}
        title="Retry step?"
        actionRow={[
          <Button
            key="retry"
            variant="primary"
            onClick={() => {
              if (
                !transactionId ||
                !transactionStep ||
                (transactionStep !== "operator" && transactionStep !== "sink")
              )
                return;

              toast
                .pushAsync(
                  retryTransaction.mutateAsync({
                    transactionId,
                    transactionRetryStage:
                      transactionStep === "operator"
                        ? "transformation"
                        : "ingestion",
                  }),
                  {
                    intent: "info",
                    description: `Re-trying ${getStepDisplayLabel(transactionStep)}`,
                    isBusy: true,
                  },
                  {
                    isDismissible: false,
                  }
                )
                .then(({ toast: firstToast }) =>
                  toast.push(
                    {
                      intent: "success",
                      isBusy: false,
                      description: `Successfully re-tried ${getStepDisplayLabel(transactionStep)}.`,
                    },
                    {
                      id: firstToast.id,
                      isDismissible: true,
                    }
                  )
                );
              setIsRetryConfirmationOpen(false);
            }}
          >
            Yes, Retry
          </Button>,
        ]}
      >
        Resubmitting a partial or failed dataset will create new rows where data
        failed in the previous submission and will not change successful rows.
        Do you want to continue?
      </Dialog>
      {isRowLevelDetailShown ? (
        <DataTablePanel
          storageKey={tableConfigStorageKey}
          panelProps={{
            title: transactionStep
              ? getStepDisplayLabel(transactionStep)
              : "Submission Data",
            titleDecorator: (
              <PillToggleGroup
                name="status"
                pills={pillToggles}
                onChange={setStatusFilter}
                isLoading={isLoadingTransactionRowLevelDetails}
              />
            ),
            actionRow,
            ...panelProps,
          }}
          dataTableProps={{
            headers,
            items,
            getItemId: (item) => item.index.toString(),
            isLoading: isLoadingTransactionRowLevelDetails,
            onSortChange: updateTableState,
            onPaginationChange: updateTableState,
            sorting: {
              sortBy: tableState.sortBy,
              sortDirection: tableState.sortDirection,
            },
            pagination,
            emptyStateProps: errorTransactionRowLevelDetails
              ? {
                  title: "No rows available",
                  variant: "error",
                  suggestion: "Row-level error information is not available",
                }
              : {
                  title: "No rows available",
                  suggestion: "Try adjusting the filters",
                },
          }}
        />
      ) : (
        <Panel
          title={
            transactionStep
              ? getStepDisplayLabel(transactionStep)
              : "Step Details"
          }
          loaded={!isLoadingTransactionRowLevelDetails}
        >
          <EmptyState
            {...(emptyStateProps ?? {
              title: "Submission in-progress",
              variant: "info",
            })}
          />
        </Panel>
      )}
    </>
  );
};
